import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import { getLangs } from "../../reduxToolkit/features/adminPanel/misc";
import { setActiveLangFront } from "../../reduxToolkit/features/miscFront";
import LoadingBox from "./Loading";
import { getDefaultLangAlias, replaceLangAlias } from "../../hooks/langAlias";

// Sits at /:langAlias for the whole storefront. Validates the alias
// against the real (active) language list, syncs it into
// activeLangFront/localStorage the same way the language switcher
// already does (so every existing per-language component keeps working
// unchanged), and redirects to a valid alias if the URL's one isn't
// recognized.
export default function LangGuard() {
  const dispatch = useDispatch();
  const { langAlias } = useParams();
  const location = useLocation();
  const { langs } = useSelector((state) => state.misc);

  // Tracks whether the langs fetch has actually settled, so an empty
  // array can be told apart from "still loading" — otherwise a fresh
  // install with zero Lang documents would spin on the loading screen
  // forever instead of falling back gracefully.
  const [langsChecked, setLangsChecked] = useState(false);

  useEffect(() => {
    if (langs && langs.length > 0) {
      setLangsChecked(true);
    } else {
      dispatch(getLangs()).finally(() => setLangsChecked(true));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const match =
    langs && langs.find((l) => l.langCode === langAlias && l.active);

  useEffect(() => {
    if (match) {
      localStorage.setItem("langFront", JSON.stringify(match));
      dispatch(setActiveLangFront(match));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match?._id]);

  if (!langsChecked) {
    return <LoadingBox w={"100%"} h={"100vh"} />;
  }

  // Failsafe: no Lang documents exist at all (fresh/misconfigured
  // install). There's nothing to validate the alias against and nowhere
  // sensible to redirect to, so just render as-is rather than looping —
  // every per-language component already defaults to "en" locally when
  // activeLangFront/langs are empty.
  if (!langs || langs.length === 0) {
    return <Outlet />;
  }

  if (!match) {
    const alias = getDefaultLangAlias(langs);
    const aliasIsValid = langs.some((l) => l.langCode === alias && l.active);
    // Only redirect if the fallback alias actually resolves to a real
    // language — otherwise (e.g. no "en" was ever added, only other
    // languages) this would just redirect into another dead end forever.
    if (aliasIsValid && alias !== langAlias) {
      return (
        <Navigate
          to={replaceLangAlias(alias, location.pathname, location.search)}
          replace
        />
      );
    }
    return <Outlet />;
  }

  return <Outlet />;
}
